/**
 * Extended fields stored in `nursing_home_claims.verification_documents` (JSON).
 * Older claims may omit these keys — admin UI should tolerate missing values.
 */

export type FacilityRelationship =
  | "direct_owner"
  | "operator_or_management"
  | "authorised_staff"
  | "marketing_or_agency"
  | "other";

export interface NursingHomeClaimFormData {
  role: string;
  email: string;
  contactPhone: string;
  verificationNote: string;
  /** Full name as on official / HR records */
  legalName: string;
  jobTitle: string;
  /** Registered operator / employer legal name */
  organizationLegalName: string;
  facilityRelationship: FacilityRelationship | "";
  officialWebsiteUrl: string;
  hiqaOrRegistrationRef: string;
  authorityConfirmation: boolean;
  verificationChannel: "email" | "phone" | "either";
  linkedInUrl: string;
  referralSource: string;
}

export const emptyNursingHomeClaimForm = (): NursingHomeClaimFormData => ({
  role: "",
  email: "",
  contactPhone: "",
  verificationNote: "",
  legalName: "",
  jobTitle: "",
  organizationLegalName: "",
  facilityRelationship: "",
  officialWebsiteUrl: "",
  hiqaOrRegistrationRef: "",
  authorityConfirmation: false,
  verificationChannel: "either",
  linkedInUrl: "",
  referralSource: "",
});

export function mergeClaimFormFromStorage(
  partial: Partial<NursingHomeClaimFormData> | null | undefined,
): NursingHomeClaimFormData {
  return { ...emptyNursingHomeClaimForm(), ...(partial ?? {}) };
}

export function isNursingHomeClaimFormComplete(f: NursingHomeClaimFormData): boolean {
  return Boolean(
    f.role &&
    f.email.trim() &&
    f.contactPhone.trim() &&
    f.legalName.trim() &&
    f.jobTitle.trim() &&
    f.organizationLegalName.trim() &&
    f.facilityRelationship &&
    f.authorityConfirmation,
  );
}

export const FACILITY_RELATIONSHIP_LABELS: Record<FacilityRelationship, string> = {
  direct_owner: "I am a direct owner / shareholder of this facility",
  operator_or_management: "I work for the operator / management company that runs this home",
  authorised_staff: "I am staff with written authority to manage this listing (e.g. manager, administrator)",
  marketing_or_agency: "I work for a marketing or PR agency acting on behalf of the home",
  other: "Other (explain below)",
};

export const REFERRAL_SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: "unset", label: "Prefer not to say" },
  { value: "search", label: "Search engine" },
  { value: "colleague", label: "Colleague / word of mouth" },
  { value: "social", label: "Social media" },
  { value: "email", label: "Email from Grandpal" },
  { value: "event", label: "Event or conference" },
  { value: "other", label: "Other" },
];

export function referralSourceLabel(value: string | null | undefined): string {
  if (!value || value === "unset") return "—";
  const o = REFERRAL_SOURCE_OPTIONS.find((x) => x.value === value);
  return o?.label ?? value;
}

/** Heuristic used for faster review when work email domain matches facility name. */
export function computeEmailDomainMatchesCentre(formEmail: string, centerName: string): boolean {
  if (!formEmail?.trim() || !centerName?.trim()) return false;
  const emailDomain = formEmail.split("@")[1]?.toLowerCase();
  const centreNameWords = centerName.toLowerCase().split(/\s+/);
  return centreNameWords.some((word) => {
    const cleaned = word.replace(/[^a-z0-9]/g, "");
    return cleaned.length > 2 && Boolean(emailDomain?.includes(cleaned));
  });
}

/** Persisted JSON for `nursing_home_claims.verification_documents`. */
export function buildVerificationDocumentsPayload(
  formData: NursingHomeClaimFormData,
  centerName: string,
): Record<string, unknown> {
  const { verificationNote, referralSource, ...rest } = formData;
  return {
    ...rest,
    note: verificationNote,
    emailVerified: computeEmailDomainMatchesCentre(formData.email, centerName),
    referralSource:
      referralSource && referralSource !== "unset" ? referralSource : null,
  };
}
